import type {
    MetricAxisSpec,
    MetricPointMarker,
    MetricSeriesPoint,
} from '@/components/charts/generic';
import { calculateChartRange } from '@/components/charts/generic';
import { formatDateShortLocalized } from '@/utils/date';
import type { Locale } from '@/utils/localeFormatters';

/**
 * The look the Health and Welfare line charts share, as MetricChart spec parts: softly curved
 * lines, hollow markers that fill on hover, a value axis that fits what the legend shows and a
 * tooltip titled with the localised day.
 */

export const HEALTH_LINE_TENSION = 0.25;

/** A ring in the series colour around the chart's surface, filled when hovered. */
export const HEALTH_POINT_MARKER: MetricPointMarker = {
    radius: 4,
    hoverRadius: 6,
    fill: 'surface',
    borderWidth: 2,
    filledOnHover: true,
};

/** The tooltip title: the hovered day, localised (`Sep 16, 2026`). */
export function healthTooltipTitle(locale: Locale): (key: string) => string {
    return (key) => formatDateShortLocalized(key, locale);
}

/**
 * A percentage axis whose bounds follow the visible series: from 0 to a rounded maximum with
 * `paddingPercent` headroom, the bounds Health and Welfare charts always had.
 */
export function healthPercentAxis(label: string, paddingPercent = 5): MetricAxisSpec {
    return {
        label,
        unit: '%',
        fitRange: (values) => calculateChartRange(values, paddingPercent, true),
    };
}

/** A share (0–1) as percent, or a gap: absent and NaN values are missing, never 0. */
export function toPercent(value: number | null | undefined): number | null {
    if (value == null || Number.isNaN(Number(value))) return null;
    return Number(value) * 100;
}

/** One series' points from the welfare days, the share read by `value` as percent. */
export function percentPoints<TDay extends { day: string }>(
    days: TDay[],
    value: (day: TDay) => number | null | undefined
): MetricSeriesPoint[] {
    return days.map((day) => ({ day: day.day, value: toPercent(value(day)) }));
}
